import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import hostelService from '../../services/hostelService';
import { House, Users, Bed, CreditCard, Warning, Plus, CaretRight, Wrench, FileText, ChatCircle } from '@phosphor-icons/react';
import './hostel.css';

function HostelDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState({
    total_hostels: 0,
    total_rooms: 0,
    occupied_rooms: 0,
    allocated_students: 0,
    fee_collection: { collected: 0, pending: 0 },
    pending_maintenance: 0,
    pending_complaints: 0
  });

  useEffect(() => {
    if (user && !['super_admin', 'school_admin', 'hostel_admin'].includes(user.role)) {
      toast.error('Access denied: Admin role required');
      navigate('/');
    }
  }, [user, navigate]);

  useEffect(() => {
    const loadMetrics = async () => {
      try {
        setLoading(true);
        const [hostelData, roomData, allocationData, feesData, maintenanceData, complaintsData] = await Promise.all([
          hostelService.listHostels().catch(() => []),
          hostelService.listRooms().catch(() => []),
          hostelService.listAccommodation().catch(() => []),
          hostelService.listFees().catch(() => []),
          hostelService.listMaintenance().catch(() => []),
          hostelService.listComplaints().catch(() => [])
        ]);

        const hostels = Array.isArray(hostelData) ? hostelData : hostelData.hostels || [];
        const rooms = Array.isArray(roomData) ? roomData : roomData.rooms || [];
        const allocations = Array.isArray(allocationData) ? allocationData : allocationData.allocations || [];
        const fees = Array.isArray(feesData) ? feesData : feesData.fees || [];
        const maintenance = Array.isArray(maintenanceData) ? maintenanceData : maintenanceData.maintenance || [];
        const complaints = Array.isArray(complaintsData) ? complaintsData : complaintsData.complaints || [];

        const activeHostels = hostels.filter(h => h.status === 'active');
        const occupiedRooms = rooms.filter(r => r.current_occupancy > 0);
        const paidFees = fees.filter(f => f.is_paid).reduce((sum, f) => sum + (f.amount_paid || 0), 0);
        const outstandingFees = fees.filter(f => !f.is_paid).reduce((sum, f) => sum + (f.amount_due || 0), 0);
        const pendingMaintenance = maintenance.filter(m => m.status === 'pending').length;
        const pendingComplaints = complaints.filter(c => c.status === 'open').length;

        setMetrics({
          total_hostels: activeHostels.length,
          total_rooms: rooms.length,
          occupied_rooms: occupiedRooms.length,
          allocated_students: allocations.length,
          fee_collection: { collected: paidFees, pending: outstandingFees },
          pending_maintenance: pendingMaintenance,
          pending_complaints: pendingComplaints
        });
      } catch (error) {
        console.error('Error loading metrics:', error);
        toast.error('Failed to load hostel metrics');
      } finally {
        setLoading(false);
      }
    };

    loadMetrics();
  }, []);

  const MetricCard = ({ icon: Icon, title, value, subtitle, color }) => (
    <div className={`metric-card metric-${color}`}>
      <div className="metric-icon">
        <Icon size={28} weight="duotone" />
      </div>
      <div className="metric-content">
        <h3>{title}</h3>
        <p className="metric-value">{value}</p>
        {subtitle && <p className="metric-subtitle">{subtitle}</p>}
      </div>
    </div>
  );

  const QuickActionButton = ({ icon: Icon, label, onClick }) => (
    <button className="quick-action" onClick={onClick}>
      <Icon size={20} />
      <span>{label}</span>
    </button>
  );

  if (loading) {
    return <Layout><div className="loading">Loading hostel dashboard...</div></Layout>;
  }

  return (
    <Layout>
      <div className="page hostel-dashboard">
        <div className="page-header">
          <div>
            <h1>Hostel Management</h1>
            <p>Manage hostels, rooms, student allocations, and accommodations</p>
          </div>
        </div>

        <div className="metrics-grid">
          <MetricCard
            icon={House}
            title="Hostels"
            value={metrics.total_hostels}
            color="primary"
          />
          <MetricCard
            icon={Bed}
            title="Rooms"
            value={metrics.total_rooms}
            subtitle={`${metrics.occupied_rooms} occupied`}
            color="info"
          />
          <MetricCard
            icon={Users}
            title="Allocated Students"
            value={metrics.allocated_students}
            color="success"
          />
          <MetricCard
            icon={CreditCard}
            title="Fee Collection"
            value={`GHS ${metrics.fee_collection.collected.toFixed(2)}`}
            subtitle={`GHS ${metrics.fee_collection.pending.toFixed(2)} pending`}
            color="warning"
          />
          <MetricCard
            icon={Wrench}
            title="Maintenance"
            value={metrics.pending_maintenance}
            subtitle="Pending issues"
            color="danger"
          />
          <MetricCard
            icon={ChatCircle}
            title="Complaints"
            value={metrics.pending_complaints}
            subtitle="Open complaints"
            color="secondary"
          />
        </div>

        <div className="dashboard-section">
          <h2>Quick Actions</h2>
          <div className="quick-actions">
            <QuickActionButton
              icon={Plus}
              label="Add Hostel"
              onClick={() => navigate('/hostel/hostels')}
            />
            <QuickActionButton
              icon={Bed}
              label="Manage Rooms"
              onClick={() => navigate('/hostel/rooms')}
            />
            <QuickActionButton
              icon={Users}
              label="Allocate Students"
              onClick={() => navigate('/hostel/allocations')}
            />
            <QuickActionButton
              icon={CreditCard}
              label="Manage Fees"
              onClick={() => navigate('/hostel/fees')}
            />
            <QuickActionButton
              icon={FileText}
              label="Attendance"
              onClick={() => navigate('/hostel/attendance')}
            />
            <QuickActionButton
              icon={Wrench}
              label="Maintenance"
              onClick={() => navigate('/hostel/maintenance')}
            />
            <QuickActionButton
              icon={ChatCircle}
              label="Complaints"
              onClick={() => navigate('/hostel/complaints')}
            />
            <QuickActionButton
              icon={Users}
              label="Visitor Logs"
              onClick={() => navigate('/hostel/visitors')}
            />
          </div>
        </div>

        <div className="dashboard-section">
          <h2>Module Navigation</h2>
          <div className="nav-links">
            <a href="/hostel/hostels" className="nav-link">
              <House size={20} />
              <span>Hostels Management</span>
              <CaretRight size={20} />
            </a>
            <a href="/hostel/rooms" className="nav-link">
              <Bed size={20} />
              <span>Rooms Management</span>
              <CaretRight size={20} />
            </a>
            <a href="/hostel/allocations" className="nav-link">
              <Users size={20} />
              <span>Student Allocations</span>
              <CaretRight size={20} />
            </a>
            <a href="/hostel/fees" className="nav-link">
              <CreditCard size={20} />
              <span>Hostel Fees</span>
              <CaretRight size={20} />
            </a>
            <a href="/hostel/attendance" className="nav-link">
              <FileText size={20} />
              <span>Attendance Tracking</span>
              <CaretRight size={20} />
            </a>
            <a href="/hostel/maintenance" className="nav-link">
              <Wrench size={20} />
              <span>Maintenance Logs</span>
              <CaretRight size={20} />
            </a>
            <a href="/hostel/complaints" className="nav-link">
              <ChatCircle size={20} />
              <span>Complaints</span>
              <CaretRight size={20} />
            </a>
            <a href="/hostel/visitors" className="nav-link">
              <Users size={20} />
              <span>Visitor Logs</span>
              <CaretRight size={20} />
            </a>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default HostelDashboardPage;
